import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, MapPin, Calendar, User, CheckCircle2,
  AlertTriangle, XCircle, Clock, Send, FileText, Sparkles,
  Shirt, ShieldCheck, Star, X,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * Read-only detail viewer for a Supervision Report. Shared across all 3
 * roles. Admin sees an "Aprovar e enviar" button when status === submitted.
 */

const SECTION_LABELS = {
  living: "Sala / Áreas Comuns",
  kitchen: "Cozinha",
  bedroom: "Quartos",
  wc: "Casas de Banho",
};

const STATE_META = {
  done:    { label: "Concluído", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", Icon: CheckCircle2 },
  pending: { label: "Pendente",  color: "text-[#a0b2af] bg-[#1a4640]/40 border-[#1a4640]",          Icon: Clock },
  issue:   { label: "Problema",  color: "text-red-400 bg-red-500/10 border-red-500/30",             Icon: XCircle },
};

const FINAL_META = {
  ready:              { label: "Pronta para Check-in",  color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/40", Icon: CheckCircle2 },
  needs_maintenance:  { label: "Necessita Manutenção",  color: "text-amber-400 bg-amber-500/10 border-amber-500/40",       Icon: AlertTriangle },
  needs_review:       { label: "Necessita Revisão",     color: "text-orange-400 bg-orange-500/10 border-orange-500/40",    Icon: AlertTriangle },
  urgent_issue:       { label: "Problema Urgente",      color: "text-red-400 bg-red-500/10 border-red-500/40",             Icon: XCircle },
};

const STATUS_META = {
  draft:     { label: "Rascunho",   color: "bg-[#1a4640] text-[#a0b2af] border-[#2a564f]" },
  submitted: { label: "Em revisão", color: "bg-[#3a2f0a] text-[#e8c860] border-[#d4af37]/40" },
  approved:  { label: "Aprovado",   color: "bg-[#0a3a33] text-[#7fd9c2] border-[#1a8868]" },
  sent:      { label: "Enviado ao cliente", color: "bg-[#0a3a33] text-[#d4af37] border-[#d4af37]" },
};

const RATING_META = {
  excellent:         { label: "Excelente",          color: "text-emerald-400" },
  good:              { label: "Boa",                color: "text-[#d4af37]" },
  needs_correction:  { label: "Necessita Correção", color: "text-orange-400" },
};

const PHOTO_CATS = [
  { key: "before", label: "Antes",      Icon: Camera },
  { key: "after",  label: "Depois",     Icon: Sparkles },
  { key: "damage", label: "Danos",      Icon: AlertTriangle },
  { key: "final",  label: "Supervisão", Icon: ShieldCheck },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function PropertyHeader({ p, status, createdAt }) {
  const sm = STATUS_META[status] || STATUS_META.draft;
  return (
    <div className="rounded-2xl border border-[#1a4640] bg-gradient-to-br from-[#062c27] to-[#041f1b] p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <span className="inline-block text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">
            Relatório de Supervisão
          </span>
          <h1 className="mt-2 font-display text-2xl sm:text-3xl text-[#fdfbf7]">
            {p?.name || "Propriedade"}
          </h1>
          {p?.address && (
            <p className="mt-2 text-sm text-[#a0b2af] flex items-center gap-1.5">
              <MapPin size={14} className="text-[#d4af37]" /> {p.address}
            </p>
          )}
        </div>
        <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-medium border ${sm.color}`}
              data-testid="detail-status">
          {sm.label}
        </span>
      </div>

      <dl className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
        <Field label="Data do serviço" value={p?.service_date} icon={<Calendar size={12} />} />
        <Field label="Hora início"    value={p?.start_time} />
        <Field label="Hora conclusão" value={p?.end_time} />
        <Field label="Duração"        value={p?.duration_min ? `${p.duration_min} min` : null} />
        <Field label="Funcionário"    value={p?.employee_name} icon={<User size={12} />} />
        <Field label="Supervisor"     value={p?.supervisor_name} icon={<ShieldCheck size={12} />} />
        <Field label="Código"         value={p?.code} />
        <Field label="Criado em"      value={fmtDate(createdAt)} />
      </dl>
    </div>
  );
}

function Field({ label, value, icon }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-[#a0b2af] flex items-center gap-1">
        {icon} {label}
      </dt>
      <dd className="mt-1 text-sm text-[#fdfbf7] truncate">{value || "—"}</dd>
    </div>
  );
}

function SectionCard({ title, icon, children, testId }) {
  return (
    <section data-testid={testId}
             className="rounded-2xl border border-[#1a4640] bg-[#062c27] overflow-hidden">
      <header className="flex items-center gap-2.5 px-6 py-4 border-b border-[#1a4640]">
        <span className="text-[#d4af37]">{icon}</span>
        <h2 className="font-display text-lg text-[#fdfbf7]">{title}</h2>
      </header>
      <div className="p-6">{children}</div>
    </section>
  );
}

function ChecklistBlock({ checklist }) {
  if (!checklist || Object.keys(checklist).length === 0) {
    return <p className="text-sm text-[#a0b2af]">Nenhum item de checklist registado.</p>;
  }
  return (
    <div className="space-y-6">
      {Object.entries(checklist).map(([key, items]) => {
        const sectionLabel = SECTION_LABELS[key] || key;
        const list = Array.isArray(items) ? items : [];
        return (
          <div key={key} data-testid={`checklist-section-${key}`}>
            <h3 className="font-display text-base text-[#fdfbf7] mb-3">
              {sectionLabel}
              <span className="ml-2 text-xs text-[#a0b2af] font-sans">({list.length} itens)</span>
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {list.map((it, idx) => {
                const m = STATE_META[it.state] || STATE_META.pending;
                const Icon = m.Icon;
                return (
                  <li key={idx}
                      className={`flex items-start gap-2.5 px-3 py-2.5 rounded-md border text-sm ${m.color}`}>
                    <Icon size={14} className="shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-[#fdfbf7]">{it.label}</div>
                      {it.note && <div className="text-xs text-[#a0b2af] mt-0.5">{it.note}</div>}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function PhotosBlock({ photos }) {
  const [activeCat, setActiveCat] = useState("before");
  const [lightbox, setLightbox] = useState(null);
  const list = (photos || []).filter(p => p.category === activeCat);
  const counts = PHOTO_CATS.reduce((acc, c) => {
    acc[c.key] = (photos || []).filter(p => p.category === c.key).length;
    return acc;
  }, {});

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-5" role="tablist">
        {PHOTO_CATS.map(c => {
          const Icon = c.Icon;
          const active = c.key === activeCat;
          return (
            <button
              key={c.key}
              onClick={() => setActiveCat(c.key)}
              data-testid={`photo-tab-${c.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium border transition-all ${
                active
                  ? "bg-[#d4af37] text-[#041f1b] border-[#d4af37]"
                  : "bg-[#041f1b] border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37]/50 hover:text-[#fdfbf7]"
              }`}
            >
              <Icon size={12} />
              {c.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                active ? "bg-[#041f1b]/20 text-[#041f1b]" : "bg-[#062c27] text-[#a0b2af]"
              }`}>
                {counts[c.key] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {list.length === 0 ? (
        <div className="py-12 text-center text-sm text-[#a0b2af] border border-dashed border-[#1a4640] rounded-xl">
          Sem fotografias nesta categoria.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {list.map(p => (
            <button key={p.id}
                    onClick={() => setLightbox(p)}
                    data-testid={`photo-${p.category}-${p.id}`}
                    className="group relative aspect-square rounded-lg overflow-hidden bg-[#041f1b] border border-[#1a4640] hover:border-[#d4af37] transition-all">
              <img src={`${BACKEND_URL}/api/files/${p.storage_path}`}
                   alt={p.caption || ""}
                   loading="lazy"
                   className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              {p.caption && (
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-[10px] text-white line-clamp-2">{p.caption}</p>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setLightbox(null)}
             data-testid="photo-lightbox">
          <button className="absolute top-4 right-4 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
                  onClick={() => setLightbox(null)}
                  aria-label="Fechar">
            <X size={20} />
          </button>
          <img src={`${BACKEND_URL}/api/files/${lightbox.storage_path}`}
               alt={lightbox.caption || ""}
               className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
               onClick={e => e.stopPropagation()} />
          {lightbox.caption && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-md bg-black/60 text-white text-sm">
              {lightbox.caption}
            </p>
          )}
        </div>
      )}
    </>
  );
}

function CheckRow({ ok, label }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle2 size={14} className="text-emerald-400" />
          : <XCircle size={14} className="text-[#1a4640]" />}
      <span className={ok ? "text-[#fdfbf7]" : "text-[#a0b2af]"}>{label}</span>
    </li>
  );
}

export default function SupervisionReportDetail({ basePath }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const role = user?.role;
  const isAdmin = role === "admin";
  const isEmployee = role === "employee";

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/supervision-reports/${id}`);
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao carregar relatório"));
      navigate(basePath);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function approveAndSend() {
    if (!confirm("Aprovar e enviar este relatório ao cliente?")) return;
    setActioning(true);
    try {
      await api.post(`/supervision-reports/${id}/approve-and-send`);
      toast.success("Relatório enviado ao cliente.");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao enviar relatório"));
    } finally {
      setActioning(false);
    }
  }

  async function submitForReview() {
    if (!confirm("Submeter este relatório para revisão pelo administrador?")) return;
    setActioning(true);
    try {
      await api.post(`/supervision-reports/${id}/submit`);
      toast.success("Relatório submetido para revisão.");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao submeter"));
    } finally {
      setActioning(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-4" data-testid="report-detail-loading">
        <div className="h-40 rounded-2xl bg-[#062c27] animate-pulse" />
        <div className="h-60 rounded-2xl bg-[#062c27] animate-pulse" />
      </div>
    );
  }
  if (!report) return null;

  const final = report.final_state ? FINAL_META[report.final_state] : null;
  const sup = report.supervision || {};
  const laundry = report.laundry || {};
  const occurrences = report.occurrences || [];
  const sigs = report.signatures || {};

  return (
    <div className="space-y-6" data-testid="supervision-report-detail">
      {/* Back + workflow actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate(basePath)}
                data-testid="report-back"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#a0b2af] hover:text-[#d4af37] transition-colors">
          <ArrowLeft size={14} /> Voltar à lista
        </button>

        <div className="flex items-center gap-2">
          {isEmployee && report.status === "draft" && (
            <button onClick={submitForReview}
                    disabled={actioning}
                    data-testid="report-submit-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#041f1b] transition-all disabled:opacity-50">
              <Send size={12} /> Submeter para revisão
            </button>
          )}
          {isAdmin && (report.status === "submitted" || report.status === "draft") && (
            <button onClick={approveAndSend}
                    disabled={actioning}
                    data-testid="report-approve-send-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all disabled:opacity-50">
              <Send size={12} /> Aprovar e enviar
            </button>
          )}
        </div>
      </div>

      <PropertyHeader p={report.property} status={report.status} createdAt={report.created_at} />

      {final && (
        <div className={`rounded-xl border p-4 flex items-center gap-3 ${final.color}`}
             data-testid="detail-final-state">
          <final.Icon size={20} />
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">Estado final</div>
            <div className="font-display text-base">{final.label}</div>
          </div>
        </div>
      )}

      <SectionCard title="Checklist Profissional" icon={<FileText size={18} />} testId="detail-checklist">
        <ChecklistBlock checklist={report.checklist} />
      </SectionCard>

      <SectionCard title="Lavandaria" icon={<Shirt size={18} />} testId="detail-laundry">
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <CheckRow ok={!!laundry.collected} label="Roupa recolhida" />
          <CheckRow ok={!!laundry.delivered} label="Roupa entregue" />
          <CheckRow ok={!!laundry.towels_verified} label="Toalhas verificadas" />
          <li className="flex items-center gap-2 text-sm">
            <span className="text-[#a0b2af]">Conjuntos:</span>
            <span className="text-[#fdfbf7] font-medium">{laundry.sets_count ?? "—"}</span>
          </li>
        </ul>
        {laundry.damaged_items && (
          <p className="mt-4 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
            <AlertTriangle size={14} className="inline mr-1.5" />
            Roupa danificada: {laundry.damaged_items}
          </p>
        )}
      </SectionCard>

      <SectionCard title="Controlo de Qualidade & Supervisão" icon={<ShieldCheck size={18} />} testId="detail-supervision">
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <CheckRow ok={!!sup.quality_ok} label="Qualidade aprovada" />
          <CheckRow ok={!!sup.organization_ok} label="Organização aprovada" />
          <CheckRow ok={!!sup.visual_ok} label="Padrão visual aprovado" />
          <CheckRow ok={!!sup.equipment_ok} label="Equipamentos funcionais" />
          <CheckRow ok={!!sup.ready_for_checkin} label="Pronta para Check-in" />
        </ul>
        {sup.rating && (
          <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1a4640] bg-[#041f1b]">
            <Star size={14} className={RATING_META[sup.rating]?.color || "text-[#d4af37]"} />
            <span className="text-xs uppercase tracking-wider text-[#a0b2af]">Avaliação:</span>
            <span className={`text-sm font-medium ${RATING_META[sup.rating]?.color}`}>
              {RATING_META[sup.rating]?.label || sup.rating}
            </span>
          </div>
        )}
        {sup.note && (
          <p className="mt-4 text-sm text-[#a0b2af] italic border-l-2 border-[#d4af37] pl-3">
            {sup.note}
          </p>
        )}
      </SectionCard>

      <SectionCard title="Ocorrências e Problemas" icon={<AlertTriangle size={18} />} testId="detail-occurrences">
        {occurrences.length === 0 ? (
          <p className="text-sm text-[#a0b2af]">Nenhuma ocorrência registada.</p>
        ) : (
          <ul className="space-y-2">
            {occurrences.map((o, i) => (
              <li key={i} className="flex items-start gap-3 px-4 py-3 rounded-md border border-amber-500/30 bg-amber-500/5">
                <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider text-amber-400 font-semibold">
                      {o.type || "issue"}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-[#a0b2af]">
                      · {o.severity || "low"}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-[#fdfbf7]">{o.description}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
        {report.general_notes && (
          <div className="mt-5 pt-5 border-t border-[#1a4640]">
            <div className="text-[10px] uppercase tracking-wider text-[#a0b2af] mb-1">Observações gerais</div>
            <p className="text-sm text-[#fdfbf7] whitespace-pre-wrap">{report.general_notes}</p>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Registo Fotográfico" icon={<Camera size={18} />} testId="detail-photos">
        <PhotosBlock photos={report.photos} />
      </SectionCard>

      <SectionCard title="Assinaturas" icon={<CheckCircle2 size={18} />} testId="detail-signatures">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-md border border-[#1a4640] bg-[#041f1b] p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#a0b2af] mb-1">Funcionário</div>
            {sigs.employee_signed ? (
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 size={16} /> <span className="text-sm">Assinado em {fmtDate(sigs.employee_signed_at)}</span>
              </div>
            ) : (
              <span className="text-sm text-[#a0b2af]">Pendente</span>
            )}
          </div>
          <div className="rounded-md border border-[#1a4640] bg-[#041f1b] p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#a0b2af] mb-1">Supervisor</div>
            {sigs.supervisor_signed ? (
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 size={16} /> <span className="text-sm">Assinado em {fmtDate(sigs.supervisor_signed_at)}</span>
              </div>
            ) : (
              <span className="text-sm text-[#a0b2af]">Pendente</span>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
